import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

/**
 * Function to convert a date to "yyyy-MM-DD hh:mm:ss" format.
 * Accepts either user-entered "MM/DD/yyyy" values (e.g. from a date picker) or values already
 * normalized to "yyyy-MM-DD hh:mm:ss" (e.g. populated directly from a form-template/search response),
 * so that already-normalized values aren't dropped as invalid.
 */
export function getISODate(date?: string): string {
  if (!date || date.trim() === '') {
    return '';
  }

  const alreadyISO = dayjs(date, 'YYYY-MM-DD HH:mm:ss', true);

  if (alreadyISO.isValid()) {
    return alreadyISO.format('YYYY-MM-DD HH:mm:ss');
  }

  const parsed = dayjs(date, 'MM/DD/YYYY');

  if (!parsed.isValid()) {
    return '';
  }

  return parsed.format('YYYY-MM-DD HH:mm:ss');
}

export function getTodayISODate() {
  const today = new Date();
  const isoDateFormat = today.toISOString().split('T')[0];
  return isoDateFormat;
}

export function getCurrentDateTimeISO(): string {
  return dayjs().format('YYYY-MM-DD HH:mm:ss');
}

export function millisToMinutesAndSeconds(millis: number) {
  const minutes = Math.floor(millis / 60000);
  const seconds = Number(((millis % 60000) / 1000).toFixed(0));

  // 2:60 should be 3:00
  if (seconds === 60) {
    return {
      minutes: minutes + 1 < 10 ? `0${minutes + 1}` : (minutes + 1).toString(),
      seconds: '00',
    };
  }

  return {
    minutes: minutes < 10 ? `0${minutes}` : minutes.toString(),
    seconds: seconds < 10 ? `0${seconds}` : seconds.toString(),
  };
}

/**
 * Function to check if a given date has passed (is in the past).
 * @param date - Date string, Date object, or dayjs-compatible format
 * @returns true if the date is in the past, false otherwise
 */
export function hasDatePassed(date: string | Date | dayjs.Dayjs | undefined | null): boolean {
  if (!date) {
    return false;
  }

  const targetDate = dayjs(date);

  if (!targetDate.isValid()) {
    return false;
  }

  return targetDate.isBefore(dayjs());
}
